import GoogleLoginButton from '@/components/GoogleLoginButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardList, Info } from 'lucide-react';
import React, { useState } from 'react';

// Debug component to show OAuth configuration
const OAuthDebug = () => {
  const [showDebug, setShowDebug] = useState(false);

  return (
    <div className="mt-6 text-xs text-muted-foreground">
      <button
        onClick={() => setShowDebug(!showDebug)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mx-auto"
      >
        <Info className="h-3 w-3" />
        {showDebug ? 'Hide' : 'Show'} OAuth Debug Info
      </button>

      {showDebug && (
        <div className="mt-3 p-3 bg-secondary/50 rounded-lg text-left space-y-1">
          <p>Client ID: {import.meta.env.GOOGLE_CLIENT_ID ? '[Configured]' : 'Not set'}</p>
          <p>Origin: {window.location.origin}</p>
          <p className="mt-2 text-muted-foreground">Make sure your Google Cloud Console has these configured:</p>
          <p>- Authorized JavaScript origins: <code className="text-xs bg-background px-1 py-0.5 rounded">{window.location.origin}</code></p>
          <p>- Authorized redirect URIs: <code className="text-xs bg-background px-1 py-0.5 rounded">{window.location.origin}</code></p>
        </div>
      )}
    </div>
  );
};

const Login = () => {

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#0a0a0a]">
        {/* Gradient background inspired by Develeap */}
        <div className="absolute inset-0">
          {/* Purple/magenta gradient orb */}
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-br from-purple-600/40 to-pink-500/30 rounded-full blur-3xl" />
          {/* Cyan/teal gradient orb */}
          <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-gradient-to-br from-cyan-500/30 to-teal-400/20 rounded-full blur-3xl" />
          {/* Additional subtle gradient */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-b from-transparent via-purple-900/10 to-transparent" />
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16 xl:px-20">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Weekly Report</span>
          </div>
          
          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-[1.1]">
            Streamline your<br />
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              weekly reporting
            </span>
          </h1>
          
          <p className="text-lg text-gray-400 max-w-md leading-relaxed mb-12">
            Track progress, manage compliance, and send professional reports to your clients with ease.
          </p>

          {/* Feature highlights */}
          <div className="space-y-4">
            {[
              'Create and send reports in minutes',
              'Track team compliance automatically',
              'Reusable email templates',
            ].map((feature, i) => (
              <div 
                key={i} 
                className="flex items-center gap-3 text-gray-300 animate-slide-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-400 to-pink-400" />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          {/* Develeap branding */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="text-sm text-gray-500">
              Powered by <span className="text-gray-400 font-medium">Develeap</span>
            </p>
          </div>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex flex-col items-center mb-10 lg:hidden">
            <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 mb-4 shadow-lg shadow-purple-500/25">
              <ClipboardList className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Weekly Report</h1>
            <p className="text-muted-foreground mt-1">by Develeap</p>
          </div>

          <Card className="border-border/50 shadow-2xl shadow-black/10">
            <CardHeader className="space-y-2 text-center pb-2">
              <CardTitle className="text-2xl font-bold">Welcome</CardTitle>
              <CardDescription className="text-base">
                Sign in to manage your weekly reports
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-6 pb-8">
              <div className="flex flex-col items-center space-y-6">
                <GoogleLoginButton />
                
                <p className="text-xs text-muted-foreground text-center max-w-xs">
                  By signing in, you agree to our terms of service and privacy policy.
                </p>
              </div>

              <OAuthDebug />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
